import { useEffect } from 'react';

import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';

import FAQSection from '~/components/FAQSection';
import FallBack from '~/components/Fallback';
import CopyrightFooter from '~/components/Layout/Footers/BrandFooter';
import type { SignupStyles } from '~/components/Signup/types';
import { usePageContext } from '~/context/usePageContext';
import { BOOST_AFFILIATE_ATTRIBUTION_WINDOW_DAYS, BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import useWebBuilderCommunicator from '~/hooks/useUtils/useWebBuilderCommunicator';
import remixI18n from '~/i18next.server';
import type { LandingPage } from '~/interfaces/landing_page';
import type { Post } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';
import getSignupWidgetConfigs from '~/pages/index/_utils/getSignupWidgetConfigs';
import RecommendationsModal from '~/pages/recommendations/components/RecommendationsModal';
import useRecommendationsSignUpModal from '~/pages/recommendations/hooks/useRecommendationsSignUpModal';
import FeaturedPostsSection from '~/pages/subscribe/FeaturedPostsSection';
import MainSection from '~/pages/subscribe/MainSection';
import TestimonialsSection from '~/pages/subscribe/TestimonialsSection';
import getRecommendingPublication from '~/services/api/boosts/getRecommendingPublication.server';
import getLandingPage from '~/services/api/landing_pages/getLandingPage.server';
import getFeaturedPosts from '~/services/api/posts/getFeaturedPosts.server';
import getPublication from '~/services/api/publication/getPublication.server';
import getWebThemev2 from '~/services/api/web_templates/getWebThemev2.server';
import * as posts from '~/services/exampleSite/archivePosts';
import getExamplePublication from '~/services/exampleSite/getExamplePublication.server';
import getExampleSubscribePage from '~/services/exampleSite/getExampleSubscribePage.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getCookieValue from '~/utils/getCookieValue/index.server';
import getMergedMetaData from '~/utils/getMergedMetaData';
import previewAllowedCheck from '~/utils/previewAllowedCheck';
import setCacheHeaders from '~/utils/setCacheHeaders/index.server';

export interface LoaderDataProps {
  hasPosts: boolean;
  publication: Publication;
  metaTitle: string;
  metaDescription: string;
  recommendingPublication: any;
  recommendingPublicationIdInUrl: string;
  recommendingPublicationIdInCookie: string;
  landingPage: LandingPage;
  featuredPosts: Post[];
  isInitialLoadFromSwarm: boolean;
  hasWebThemesEnabled: boolean;
  signupWidgetConfigs: SignupStyles;
  cardStyles: any;
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = data?.publication;
  const metaTitle = data?.metaTitle;
  const metaDescription = data?.metaDescription || publication?.description;
  const renderableFonts = data?.landingPage?.renderable_fonts || [];
  const links: any = [];

  if (renderableFonts?.length > 0) {
    renderableFonts.forEach((font: string) => {
      links.push({
        rel: 'stylesheet',
        href: font,
        tagName: 'link',
      });
    });
  }

  if (data?.landingPage?.main_bg_image_url) {
    links.push({
      rel: 'preload',
      href: data?.landingPage?.main_bg_image_url,
      as: 'image',
    });
  }

  if (data?.landingPage?.main_image_url) {
    links.push({
      rel: 'preload',
      href: data?.landingPage?.main_image_url,
      as: 'image',
    });
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      title: metaTitle,
      description: metaDescription,
    },
    links,
  });
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const headers: HeadersFunction = ({ actionHeaders, loaderHeaders, parentHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': 'orchid-page',
  };
};

export const loader: LoaderFunction = async ({ request }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const isDraft = searchParams.get('version') === 'draft';
  const isExampleSite = searchParams.get('example_site') === 'true';
  const showExampleSite = isDraft && isExampleSite;
  const forceNewWebBuilder = searchParams.get('new_web_builder') === 'true';

  const { instanceToFetch, isInitialLoadFromSwarm } = previewAllowedCheck(request);

  const cookies: string | null = request.headers.get('Cookie');
  const recommendingPublicationIdInCookie = cookies ? getCookieValue('bhba', cookies) : undefined;

  const recommendingPublicationIdInUrl = new URL(request.url).searchParams.get('_bhba');

  const recommendingPublicationId = recommendingPublicationIdInCookie || recommendingPublicationIdInUrl;

  const promises = [
    getPublication(request, session),
    getFeaturedPosts(request, session),
    getLandingPage(request, session, {
      slug: '/subscribe',
      status: instanceToFetch,
    }),
    getWebThemev2(request, session, {
      status: instanceToFetch,
    }),
  ];

  if (recommendingPublicationId) {
    promises.push(
      getRecommendingPublication({
        request,
        recommendingPublicationId,
      }),
    );
  }

  const [publication, featuredPosts, landingPage, webTheme, recommendingPublication] = await Promise.all(promises);

  // We want users to be able to update their publication's design in the DesignLab.
  if (isInitialLoadFromSwarm) {
    publication.private = false;
  }

  if (publication.private && !isInitialLoadFromSwarm) {
    const searchParams = url.searchParams.toString();
    const redirectUrl = searchParams ? `/?${searchParams}` : '/';
    return redirect(redirectUrl);
  }

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  const cardFillEnabled = landingPage?.featured_posts_section.card.fill_enabled;
  const cardBackgroundColor = landingPage?.featured_posts_section.card.bg_color;
  const borderRadius = landingPage?.featured_posts_section.card.border_radius;
  const borderColor = landingPage?.featured_posts_section.card.border_color;
  const headingColor = landingPage?.featured_posts_section.card.text_color;
  const headingFontFamily = landingPage?.featured_posts_section.card.font;
  const descriptionFontFamily = landingPage?.featured_posts_section.card.font;
  const descriptionFontColor = landingPage?.featured_posts_section.card.body_text_color;
  const premiumBgColor = landingPage?.featured_posts_section.card.premium_bg_color;
  const premiumTextColor = landingPage?.featured_posts_section.card.premium_text_color;
  const detailsColor = landingPage?.featured_posts_section.card.details_color;
  const authorsTextColor = landingPage?.featured_posts_section.card.authors_text_color;
  const tagsEnabled = landingPage?.featured_posts_section.card.tags_enabled;
  const tagsBgColor = landingPage?.featured_posts_section.card.tags_bg_color;
  const tagsTextColor = landingPage?.featured_posts_section.card.tags_text_color;

  const cardStyles = {
    bg_color: cardFillEnabled ? cardBackgroundColor : '',
    is_image_radius_applied: !cardFillEnabled,
    border_color: cardFillEnabled ? borderColor : '',
    text_color: headingColor,
    heading_font_family: headingFontFamily,
    heading_font_color: headingColor,
    description_font_family: descriptionFontFamily,
    description_font_color: descriptionFontColor,
    premium_bg_color: premiumBgColor,
    premium_text_color: premiumTextColor,
    timefields_enabled: webTheme?.web_theme.post_insights_time_fields_enabled, // Only set at global level
    likes_comments_enabled: webTheme?.web_theme.post_insights_likes_comments_enabled, // Only set at global level
    timestamp_text_color: detailsColor,
    likes_comments_color: detailsColor,
    tags_enabled: tagsEnabled,
    tags_bg_color: tagsBgColor,
    tags_text_color: tagsTextColor,
    authors_text_color: authorsTextColor,
    border_radius: borderRadius,
    font: headingFontFamily,
  };

  return json(
    {
      publication: showExampleSite ? getExamplePublication(publication) : publication,
      recommendingPublication,
      recommendingPublicationIdInUrl,
      recommendingPublicationIdInCookie,
      hasPosts: publication.has_live_web_posts,
      landingPage: showExampleSite ? getExampleSubscribePage(landingPage) : landingPage,
      featuredPosts: showExampleSite ? posts.featuredPosts : featuredPosts,
      requestUrl: url.href,
      metaTitle: landingPage?.seo_section?.title || `${t('meta.titles.subscribe')} | ${publication.name}`,
      metaDescription: landingPage?.seo_section?.description || '',
      isInitialLoadFromSwarm,
      hasWebThemesEnabled: webTheme?.feature_enabled || forceNewWebBuilder,
      signupWidgetConfigs: getSignupWidgetConfigs({
        webTheme: webTheme.web_theme,
        overrides: {
          buttonBgColor: landingPage?.main_section.signup_bg_color,
          buttonTextColor: landingPage?.main_section.signup_text_color,
          fontFamily: landingPage?.main_section.signup_cta_font_family,
          buttonText: landingPage?.main_section.signup_cta_text,
          placeholderText: landingPage?.main_section.signup_placeholder_text,
          borderColor: landingPage?.main_section.signup_border_color,
        },
      }),
      cardStyles,
    },
    {
      // Note: Keep MaxAge 0 to avoid caching in the browser, we only want to cache via the CDN with sMaxAge.
      headers: setCacheHeaders({
        maxAge: 0,
        sMaxAge: 60 * 60 * 24 * 30, // 30 days
        staleWhileRevalidate: 60,
      }),
    },
  );
};

const Subscribe = () => {
  const {
    hasPosts,
    publication,
    recommendingPublication,
    recommendingPublicationIdInUrl,
    recommendingPublicationIdInCookie,
    landingPage,
    featuredPosts,
    isInitialLoadFromSwarm,
    hasWebThemesEnabled,
    signupWidgetConfigs,
  } = useTypedLoaderData<LoaderDataProps>();

  const { setCookie, removeCookie } = usePageContext();

  // Throw RefreshPageError if loader data is invalid [WEB-5443]
  if (!publication) {
    throw new Error('RefreshPageError');
  }

  useWebBuilderCommunicator({
    isInitialLoadFromSwarm,
    publicationId: publication.id,
  });

  const renderProperFooterColor = (): string => {
    if (landingPage?.faq_section?.enabled) {
      return landingPage.faq_section.headline_color;
    }

    if (landingPage?.featured_posts_section.enabled) {
      return landingPage.featured_posts_section.headline_color;
    }

    if (landingPage?.testimonials_section.enabled) {
      return landingPage.testimonials_section.headline_color;
    }

    return landingPage.main_section.headline_color;
  };

  const { isModalOpen } = useRecommendationsSignUpModal();

  /*
   * User lands on this page with a get param "_bhba=:recommendingPublicationId"
   * Set a cookie if it doesn't exist and direct link is on
   * or
   * Delete the cookie if it exists and direct link is off
   */
  useEffect(() => {
    if (!recommendingPublication) {
      return;
    }

    if (
      recommendingPublication.can_signup === true &&
      recommendingPublicationIdInUrl &&
      !recommendingPublicationIdInCookie
    ) {
      setCookie({
        name: BOOST_AFFILIATE_COOKIE_NAME,
        value: recommendingPublicationIdInUrl,
        days: BOOST_AFFILIATE_ATTRIBUTION_WINDOW_DAYS,
      });
    } else if (
      recommendingPublication.can_signup === false &&
      recommendingPublicationIdInCookie === recommendingPublication.id
    ) {
      removeCookie(BOOST_AFFILIATE_COOKIE_NAME);
    }
  }, [
    recommendingPublicationIdInUrl,
    recommendingPublicationIdInCookie,
    recommendingPublication,
    setCookie,
    removeCookie,
  ]);

  if (recommendingPublication && recommendingPublication.can_signup === false) {
    return (
      <FallBack
        isFullWidth
        customTitle={'Uh oh, it looks like this link is expired.'}
        customDescription={
          <>
            <strong>{recommendingPublication.name}</strong> needs to update a few things on their end to resume. In the
            meantime, you can choose to signup to {recommendingPublication.name}{' '}
            <strong>
              <a href={`${recommendingPublication.url}subscribe`}>here</a>
            </strong>
            .
          </>
        }
        errorCode="404"
        publicationPresent={false}
      />
    );
  }

  if (hasWebThemesEnabled) {
    return (
      <div>
        <MainSection
          hasPosts={hasPosts}
          publication={publication}
          landingPage={landingPage}
          signupWidgetConfigs={signupWidgetConfigs}
        />
        {landingPage?.testimonials_section.enabled && (
          <TestimonialsSection
            testimonialsSection={landingPage.testimonials_section}
            testimonials={landingPage.landing_page_testimonials}
          />
        )}
        {landingPage?.featured_posts_section.enabled && (
          <FeaturedPostsSection featuredPosts={featuredPosts} landingPage={landingPage} />
        )}
        {landingPage?.faq_section?.enabled && (
          <FAQSection faq_section={landingPage.faq_section} isFirstQuestionOpen={isInitialLoadFromSwarm} />
        )}
        <div className="relative">
          <CopyrightFooter
            utmSource={publication.name}
            beehiivBranding={publication.beehiiv_branding}
            color={renderProperFooterColor()}
          />
        </div>
        {isModalOpen && <RecommendationsModal isOpen={isModalOpen} onClose={() => {}} />}
      </div>
    );
  }

  return (
    <div>
      <MainSection hasPosts={hasPosts} publication={publication} landingPage={landingPage} />
      {landingPage?.testimonials_section.enabled && (
        <TestimonialsSection
          testimonialsSection={landingPage.testimonials_section}
          testimonials={landingPage.landing_page_testimonials}
        />
      )}
      {landingPage?.featured_posts_section.enabled && (
        <FeaturedPostsSection featuredPosts={featuredPosts} landingPage={landingPage} />
      )}
      {landingPage?.faq_section?.enabled && (
        <FAQSection faq_section={landingPage.faq_section} isFirstQuestionOpen={isInitialLoadFromSwarm} />
      )}
      <div className="relative">
        <CopyrightFooter
          utmSource={publication.name}
          beehiivBranding={publication.beehiiv_branding}
          color={renderProperFooterColor()}
        />
      </div>
      {isModalOpen && <RecommendationsModal isOpen={isModalOpen} onClose={() => {}} />}
    </div>
  );
};

export default Subscribe;
